import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";
import headerImage from "@/assets/headers/header-trabalhos.jpg";

const TrabalhosEspirituais = () => {
  const { t } = useTranslation();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Trabalhos Espirituais de Umbanda",
    "provider": {
      "@type": "ReligiousOrganization",
      "name": "TUPJA - Terreiro Pai Joaquim de Aruanda"
    },
    "areaServed": "Orlando, FL",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Serviços Espirituais",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Passe Espiritual",
            "description": "Limpeza energética básica realizada pelos Guias espirituais"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Consulta Espiritual",
            "description": "Atendimento individual com os Guias para orientação"
          }
        }
      ]
    }
  };

  return (
    <Layout>
      <SEO 
        title={t('spiritualWorks.title')}
        description={t('spiritualWorks.types.pass.desc')}
        keywords="passe espiritual, consulta espiritual, limpeza espiritual, descarrego, trabalhos umbanda orlando, giras sábado, atendimento espiritual"
        canonicalUrl={`${window.location.origin}/trabalhos-espirituais`}
        structuredData={structuredData}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${headerImage})` }}
        />
        <div className="absolute inset-0 bg-logo-brown-dark/70" />
        <div className="relative z-10 text-center px-4 container mx-auto">
          <h1 className="font-display text-5xl md:text-7xl text-logo-beige mb-6 font-bold">
            {t('spiritualWorks.title')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto font-body">
            {t('spiritualWorks.subtitle')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Tipos de Atendimento */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('spiritualWorks.types.title')}
            </h2>
            
            <div className="space-y-6">
              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h3 className="font-display text-2xl text-sand-dark mb-3">{t('spiritualWorks.types.pass.title')}</h3>
                <p className="text-foreground/80 leading-relaxed">{t('spiritualWorks.types.pass.desc')}</p>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h3 className="font-display text-2xl text-sand-dark mb-3">{t('spiritualWorks.types.consultation.title')}</h3>
                <p className="text-foreground/80 leading-relaxed">{t('spiritualWorks.types.consultation.desc')}</p>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h3 className="font-display text-2xl text-sand-dark mb-3">{t('spiritualWorks.types.cleansing.title')}</h3>
                <p className="text-foreground/80 leading-relaxed">{t('spiritualWorks.types.cleansing.desc')}</p>
              </div>

              <div className="bg-sand-light/20 p-6 rounded-lg border border-sand-dark/10">
                <h3 className="font-display text-2xl text-sand-dark mb-3">{t('spiritualWorks.types.blessings.title')}</h3>
                <p className="text-foreground/80 leading-relaxed">{t('spiritualWorks.types.blessings.desc')}</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-sand-dark/20 my-12" />

          {/* Horários das Giras */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('spiritualWorks.schedule.title')}
            </h2>
            
            <div className="bg-gradient-to-br from-sand-light/30 to-sand-light/10 p-8 rounded-lg border border-sand-dark/20 text-center">
              <p className="font-display text-3xl text-sand-dark mb-2">{t('spiritualWorks.schedule.day')}</p>
              <p className="text-2xl text-foreground/80">{t('spiritualWorks.schedule.time')}</p>
              <p className="text-foreground/60 mt-4">{t('spiritualWorks.schedule.timeAlt')}</p>
            </div>
          </div>

          <div className="h-px bg-sand-dark/20 my-12" />

          {/* Como Funcionam */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('spiritualWorks.howItWorks.title')}
            </h2>
            
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <div>
                <h4 className="font-display text-xl text-sand-dark mb-2">{t('spiritualWorks.howItWorks.arrival.title')}</h4>
                <p>{t('spiritualWorks.howItWorks.arrival.desc')}</p>
              </div>

              <div>
                <h4 className="font-display text-xl text-sand-dark mb-2">{t('spiritualWorks.howItWorks.opening.title')}</h4>
                <p>{t('spiritualWorks.howItWorks.opening.desc')}</p>
              </div>

              <div>
                <h4 className="font-display text-xl text-sand-dark mb-2">{t('spiritualWorks.howItWorks.incorporation.title')}</h4>
                <p>{t('spiritualWorks.howItWorks.incorporation.desc')}</p>
              </div>

              <div>
                <h4 className="font-display text-xl text-sand-dark mb-2">{t('spiritualWorks.howItWorks.service.title')}</h4>
                <p>{t('spiritualWorks.howItWorks.service.desc')}</p>
              </div>

              <div>
                <h4 className="font-display text-xl text-sand-dark mb-2">{t('spiritualWorks.howItWorks.closing.title')}</h4>
                <p>{t('spiritualWorks.howItWorks.closing.desc')}</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-sand-dark/20 my-12" />

          {/* Como se Preparar */}
          <div className="mb-16">
            <h2 className="font-display text-4xl text-sand-dark mb-8 drop-shadow-[0_2px_4px_rgba(28,45,74,0.15)]">
              {t('spiritualWorks.preparation.title')}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-display text-2xl text-sand-dark mb-4">{t('spiritualWorks.preparation.before.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('spiritualWorks.preparation.before.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-display text-2xl text-sand-dark mb-4">{t('spiritualWorks.preparation.after.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('spiritualWorks.preparation.after.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-display text-2xl text-sand-dark mb-4">{t('spiritualWorks.preparation.bring.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('spiritualWorks.preparation.bring.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-display text-2xl text-sand-dark mb-4">{t('spiritualWorks.preparation.wear.title')}</h4>
                <ul className="space-y-2 text-foreground/80">
                  {(t('spiritualWorks.preparation.wear.items', { returnObjects: true }) as string[]).map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
    </Layout>
  );
};

export default TrabalhosEspirituais;